import { useEffect, useRef, type HTMLAttributes, type ReactNode } from "react";
import type { PageNode } from "../../../shared/src/pages";
import { Accordion, AccordionItem } from "./ui-kit/accordion";
import { Tabs } from "./ui-kit/tabs";

type Panel = Extract<PageNode, { kind: "frame" }>;
type Interaction = Extract<PageNode, { kind: "tabs" | "accordion" }>;

/** One trusted renderer for interactive JSON elements on the site and both editor canvases. */
export function PageInteraction({
  node,
  attributes,
  renderPanel,
}: {
  node: Interaction;
  attributes?: HTMLAttributes<HTMLDivElement> & Record<string, unknown>;
  renderPanel: (panel: Panel) => ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const panels = node.children.filter((panel): panel is Panel => panel.kind === "frame");
  const index =
    node.initialIndex >= 0 && node.initialIndex < panels.length
      ? node.initialIndex
      : node.kind === "tabs"
        ? 0
        : -1;
  const initial = panels[index]?.id ?? "";
  const multiple = node.kind === "accordion" ? node.multiple : undefined;
  useEffect(() => {
    if (node.kind !== "accordion") return;
    const group = root.current?.querySelector<HTMLElement>('[data-ui="accordion"]');
    if (!group) return;
    // Native disclosure state survives ordinary React edits. Changing its authored defaults
    // resets every item, including ones opened by the visitor since the previous render.
    const items = [...group.querySelectorAll<HTMLDetailsElement>("details")].filter(
      (item) => item.closest('[data-ui="accordion"]') === group,
    );
    items.forEach((item) => (item.open = false));
    items.forEach((item) => (item.open = item.id === `page-${node.id}-disclosure-${initial}`));
  }, [initial, multiple, node.kind, node.id]);
  if (!panels.length) return null;
  const id = `page-${node.id}`;
  return (
    <div
      ref={root}
      {...attributes}
      className={["drate-page-node drate-page-interaction", attributes?.className]
        .filter(Boolean)
        .join(" ")}
      data-page-interaction-initial={initial}
      data-page-interaction-multiple={node.kind === "accordion" ? String(node.multiple) : undefined}
    >
      {node.kind === "tabs" ? (
        <Tabs
          id={`${id}-tabs`}
          label={node.label}
          value={initial}
          tabs={panels.map((panel, position) => ({
            value: panel.id,
            label: panel.panelTitle || `Panel ${position + 1}`,
          }))}
          panels={Object.fromEntries(panels.map((panel) => [panel.id, renderPanel(panel)]))}
        />
      ) : (
        <Accordion id={`${id}-accordion`} label={node.label}>
          {panels.map((panel, position) => (
            <AccordionItem
              key={panel.id}
              id={`${id}-disclosure-${panel.id}`}
              title={panel.panelTitle || `Panel ${position + 1}`}
              name={node.multiple ? undefined : `${id}-accordion`}
              open={panel.id === initial}
            >
              {renderPanel(panel)}
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
